import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import ActivityTimeline from "../components/ActivityTimeline";
import api, { formatDate } from "../lib/api";
import { readApiError } from "../lib/apiError";
import { leadMatchesQuery } from "../lib/leadSearch";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  computeStalledLeads,
  isCrmPlatformAuthorityEnabled,
  mapAggregatesToStats,
  mapCrmActivity,
  mapPartyToLeadRow,
  currentIntroducerSubject,
} from "../lib/crmAdmin";
import { 
  Users, TrendingUp, Clock, AlertTriangle,
  UserPlus, RefreshCw, Download, Filter, Search, Eye,
  ChevronRight, Lock, Unlock, Building, Phone, Mail,
  Calendar, Activity, History, ArrowRight, Plus, Edit
} from "lucide-react";

const LEAD_STATUS_COLORS = {
  new_uncontacted: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  contacted: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  qualified_lead: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  account_setup: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  funded_initial: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  active_client: "bg-teal-500/20 text-teal-400 border-teal-500/30",
  strategic_client: "bg-burgundy/20 text-burgundy border-burgundy/30"
};

const CAPITAL_BAND_COLORS = {
  "250_500": "bg-blue-500/20 text-blue-400",
  "500_1m": "bg-purple-500/20 text-purple-400",
  "1m_plus": "bg-emerald-500/20 text-emerald-400",
  "unknown": "bg-gray-500/20 text-gray-400"
};

const LeadManagementPage = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [enums, setEnums] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState({ owners: [], introducers: [] });
  const [stalledLeads, setStalledLeads] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [capitalFilter, setCapitalFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modals
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  
  // Form states
  const [newLead, setNewLead] = useState({
    email: "", name: "", phone: "", company: "",
    source: "website", capital_band: "unknown", notes: ""
  });
  const [statusTransition, setStatusTransition] = useState({ new_status: "", note: "" });

  // STARSEED-CRM-012: platform-detail state (only populated on the
  // EchoSense CRM data path — ownership/notes/history live in the CRM,
  // not on the list row).
  const [platformDetail, setPlatformDetail] = useState(null);
  const [platformNotes, setPlatformNotes] = useState([]);
  const [platformHistory, setPlatformHistory] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "superuser";

  // STARSEED-CRM-012 cutover switch (010's authority flip, workload-local
  // half). Fail-closed: only an explicit DB-flag `true` routes reads and
  // writes through the platform CRM; anything else keeps the legacy Mongo
  // path exactly as before (the designed rollback lever until CRM-014).
  const platformCrm = isCrmPlatformAuthorityEnabled(user);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (platformCrm) {
        // EchoSense CRM data path: proxy → 008 bridge → /v1/crm/*.
        // Enums stay on the constants endpoint (shared stage vocabulary,
        // no Mongo read); assignable users stay on the users system.
        const [partiesRes, aggsRes, enumsRes, usersRes] = await Promise.all([
          api.get("/crm/admin/parties?limit=500"),
          api.get("/crm/admin/aggregates"),
          api.get("/managed-leads/enums/all"),
          api.get("/users/assignable")
        ]);
        const rows = (partiesRes.data.parties || []).map(mapPartyToLeadRow);
        setLeads(rows);
        setStats(mapAggregatesToStats(aggsRes.data.aggregates));
        setEnums(enumsRes.data);
        setAssignableUsers(usersRes.data);
        // No platform stalled endpoint yet (STARSEED-CRM-019) — computed
        // client-side over the fetched list, same 14-day rule.
        setStalledLeads(isAdmin ? computeStalledLeads(rows) : []);
      } else {
        const [leadsRes, statsRes, enumsRes, usersRes] = await Promise.all([
          api.get("/managed-leads"),
          api.get("/managed-leads/stats"),
          api.get("/managed-leads/enums/all"),
          api.get("/users/assignable")
        ]);
        setLeads(leadsRes.data);
        setStats(statsRes.data);
        setEnums(enumsRes.data);
        setAssignableUsers(usersRes.data);

        // Fetch stalled leads for admin
        if (isAdmin) {
          const stalledRes = await api.get("/managed-leads/stalled");
          setStalledLeads(stalledRes.data.stalled_leads || []);
        }
      }
    } catch (error) {
      toast.error("Failed to load leads data");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, platformCrm]);

  // Detail enrichment on the platform path: introducer/ownership, notes,
  // and stage history (status_change activities) live in the CRM.
  const loadPlatformDetail = useCallback(async (leadId) => {
    try {
      const [detailRes, notesRes, actsRes] = await Promise.all([
        api.get(`/crm/admin/parties/${leadId}`),
        api.get(`/crm/admin/parties/${leadId}/notes`),
        api.get(`/crm/admin/parties/${leadId}/activities`),
      ]);
      setPlatformDetail(detailRes.data.party || null);
      setPlatformNotes(notesRes.data.notes || []);
      setPlatformHistory(
        (actsRes.data.activities || [])
          .filter((a) => a.activityType === "status_change")
          .map(mapCrmActivity)
          .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
      );
    } catch (error) {
      toast.error("Failed to load CRM detail");
    }
  }, []);

  const openDetail = (lead) => {
    setSelectedLead(lead);
    setPlatformDetail(null);
    setPlatformNotes([]);
    setPlatformHistory([]);
    setNewNote("");
    setShowDetailModal(true);
    if (platformCrm) loadPlatformDetail(lead.id);
  };

  const addPlatformNote = async () => {
    if (!selectedLead || !newNote.trim()) return;
    setAddingNote(true);
    try {
      await api.post(`/crm/admin/parties/${selectedLead.id}/notes`, { body: newNote.trim() });
      setNewNote("");
      toast.success("Note added");
      await loadPlatformDetail(selectedLead.id);
    } catch (error) {
      toast.error(readApiError(error, "Failed to add note"));
    } finally {
      setAddingNote(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createLead = async () => {
    try {
      if (platformCrm) {
        await api.post("/crm/admin/parties", newLead);
      } else {
        await api.post("/managed-leads", newLead);
      }
      toast.success("Lead created successfully");
      setShowCreateModal(false);
      setNewLead({ email: "", name: "", phone: "", company: "", source: "website", capital_band: "unknown", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(readApiError(error, "Failed to create lead"));
    }
  };

  const updateLeadStatus = async () => {
    if (!selectedLead || !statusTransition.new_status) return;
    try {
      const res = platformCrm
        ? await api.post(`/crm/admin/parties/${selectedLead.id}/stage`, {
            to_stage: statusTransition.new_status,
            note: statusTransition.note || null,
          })
        : await api.put(`/managed-leads/${selectedLead.id}/status`, statusTransition);
      toast.success(res.data.message);
      setShowStatusModal(false);
      setStatusTransition({ new_status: "", note: "" });
      fetchData();
    } catch (error) {
      toast.error(readApiError(error, "Failed to update status"));
    }
  };

  const updateLeadField = async (leadId, field, value) => {
    try {
      await api.put(`/managed-leads/${leadId}`, { [field]: value });
      toast.success("Lead updated");
      fetchData();
    } catch (error) {
      toast.error(readApiError(error, "Failed to update lead"));
    }
  };

  const migrateLeads = async () => {
    try {
      const res = await api.post("/managed-leads/migrate-from-leads");
      toast.success(`Migrated ${res.data.migrated} leads (${res.data.skipped} skipped)`);
      fetchData();
    } catch (error) {
      toast.error("Migration failed");
    }
  };

  const exportLeads = () => {
    const headers = "Name,Email,Phone,Company,Status,Capital Band,Owner,Introducer,Created\n";
    const csv = leads.map(l => 
      `"${l.name || ''}","${l.email || ''}","${l.phone || ''}","${l.company || ''}","${enums?.lead_status_labels[l.lead_status] || l.lead_status}","${enums?.capital_band_labels[l.capital_band] || ''}","${l.assigned_owner?.full_name || ''}","${l.introducer?.full_name || ''}","${l.created_at}"`
    ).join('\n');
    
    const blob = new Blob([headers + csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `managed_leads_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success("Leads exported");
  };

  const filteredLeads = leads.filter(lead => {
    if (statusFilter !== "all" && lead.lead_status !== statusFilter) return false;
    if (capitalFilter !== "all" && lead.capital_band !== capitalFilter) return false;
    // CAPMARKETS-293: phone digits are compared only for a phone-shaped query
    // (lib/leadSearch.js) — so "4353001520" finds "(435) 300-1520", and
    // "mike2@acme.com" no longer matches every phone containing a 2.
    return leadMatchesQuery(lead, searchQuery);
  });

  const getStatusBadge = (status) => (
    <Badge className={`${LEAD_STATUS_COLORS[status] || "bg-gray-500/20 text-gray-400 border-gray-500/30"} border`}>
      {status ? (enums?.lead_status_labels[status] || status) : "No stage"}
    </Badge>
  );

  const getCapitalBadge = (band) => (
    <Badge className={CAPITAL_BAND_COLORS[band]}>
      {enums?.capital_band_labels[band] || band}
    </Badge>
  );

  // CAPMARKETS-149: the same badges inside a Dialog. Dialogs render outside
  // `.dashboard-shell`, so the light-theme remap that darkens `text-blue-400`
  // and friends in the table never reaches them — on the Lead Detail modal's
  // cream card the capital badge measured below 2:1. Keep each tint, use the
  // flipping ink. (Separate helpers, not a flag, so the contrast guard can
  // follow each one to the surface it is actually drawn on.)
  const getStatusBadgeInDialog = (status) => (
    <Badge className={`${LEAD_STATUS_COLORS[status] || "bg-gray-500/20 text-gray-400 border-gray-500/30"} border text-cream`}>
      {status ? (enums?.lead_status_labels[status] || status) : "No stage"}
    </Badge>
  );

  const getCapitalBadgeInDialog = (band) => (
    <Badge className={`${CAPITAL_BAND_COLORS[band] || "bg-gray-500/20"} text-cream`}>
      {enums?.capital_band_labels[band] || band}
    </Badge>
  );

  return (
    <DashboardLayout
      title="Lead Management"
      subtitle={platformCrm
        ? "Institutional-grade lead tracking and conversion pipeline · EchoSense CRM"
        : "Institutional-grade lead tracking and conversion pipeline"}
    >
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {enums?.lead_statuses?.map((status) => (
          <Card 
            key={status} 
            className={`bg-surface border-platinum/10 cursor-pointer hover:border-white/20 transition-colors ${statusFilter === status ? 'border-burgundy' : ''}`}
            onClick={() => setStatusFilter(statusFilter === status ? "all" : status)}
          >
            <CardContent className="p-3">
              <p className="text-platinum/60 text-xs truncate">{enums?.lead_status_labels[status]}</p>
              <p className="text-xl font-semibold text-slate-900">{stats?.by_status?.[status] || 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Stalled Leads Alert */}
      {isAdmin && stalledLeads.length > 0 && (
        <Card className="bg-yellow-500/10 border-yellow-500/30 mb-6">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-yellow-500" size={24} />
              <div>
                <p className="text-yellow-400 font-medium">{stalledLeads.length} Stalled Lead(s)</p>
                <p className="text-yellow-400/60 text-sm">Qualified leads with no activity for 14+ days</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
              onClick={() => setStatusFilter("qualified_lead")}
            >
              View Stalled
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <Input
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-64 bg-surface border-platinum/20"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 bg-surface border-platinum/20">
              <SelectValue placeholder="Filter by Status" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-platinum/10">
              <SelectItem value="all">All Statuses</SelectItem>
              {enums?.lead_statuses?.map(s => (
                <SelectItem key={s} value={s}>{enums.lead_status_labels[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={capitalFilter} onValueChange={setCapitalFilter}>
            <SelectTrigger className="w-40 bg-surface border-platinum/20">
              <SelectValue placeholder="Capital Band" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-platinum/10">
              <SelectItem value="all">All Bands</SelectItem>
              {enums?.capital_bands?.map(b => (
                <SelectItem key={b} value={b}>{enums.capital_band_labels[b]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchData} className="border-platinum/20">
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={exportLeads} className="border-platinum/20">
            <Download size={16} className="mr-2" />
            Export
          </Button>
          {isAdmin && !platformCrm && (
            /* Mongo-side promotion (db.leads → managed_leads); hidden on the
               EchoSense CRM path — the capture-funnel promotion re-points
               with STARSEED-CRM-017. */
            <Button variant="outline" onClick={migrateLeads} className="border-platinum/20">
              <ArrowRight size={16} className="mr-2" />
              Migrate Legacy
            </Button>
          )}
          <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm">
            <Plus size={16} className="mr-2" />
            New Lead
          </Button>
        </div>
      </div>

      {/* Leads Table */}
      <Card className="bg-surface border-platinum/10">
        <CardHeader>
          <CardTitle className="text-white font-serif">Managed Leads ({filteredLeads.length})</CardTitle>
          <CardDescription>Click on a lead to view details and manage status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-platinum/10">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Lead</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Capital</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Owner</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Introducer</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Last Activity</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr 
                    key={lead.id} 
                    className="border-b border-platinum/10 hover:bg-white/5 cursor-pointer"
                    onClick={() => openDetail(lead)}
                  >
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-white font-medium">{lead.name || '-'}</p>
                        {/* CAPMARKETS-293: show whichever identifier the lead
                            has. Rendering `{lead.email}` alone left a phone-only
                            lead with a blank second line — it read as a lead
                            with no contact details at all. */}
                        <p className="text-gray-400 text-sm">{lead.email || lead.phone || '—'}</p>
                        {lead.email && lead.phone && (
                          <p className="text-gray-500 text-xs">{lead.phone}</p>
                        )}
                        {lead.company && <p className="text-gray-500 text-xs">{lead.company}</p>}
                      </div>
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(lead.lead_status)}</td>
                    <td className="py-3 px-4">{getCapitalBadge(lead.capital_band)}</td>
                    <td className="py-3 px-4">
                      {platformCrm ? (
                        /* Owner/introducer aren't on the CRM list response
                           yet (STARSEED-CRM-019); the introducer shows on
                           the detail view via ownership. */
                        <span className="text-gray-500 text-sm">—</span>
                      ) : lead.assigned_owner_user_id ? (
                        <span className="text-gray-300 text-sm">
                          {assignableUsers.owners?.find(u => u.id === lead.assigned_owner_user_id)?.full_name || 'Assigned'}
                        </span>
                      ) : (
                        <span className="text-gray-500 text-sm">Unassigned</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {platformCrm ? (
                        <span className="text-gray-500 text-sm">—</span>
                      ) : (
                        <div className="flex items-center gap-1">
                          {lead.introducer_locked && <Lock size={12} className="text-yellow-500" />}
                          <span className="text-gray-300 text-sm">
                            {assignableUsers.introducers?.find(u => u.id === lead.introducer_user_id)?.full_name || '-'}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-400 text-sm">
                      {formatDate(lead.last_meaningful_action_at)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setSelectedLead(lead); 
                          setShowStatusModal(true); 
                        }}
                        className="text-burgundy hover:text-burgundy-light"
                      >
                        <ChevronRight size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
                {filteredLeads.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-gray-500">
                      {loading ? "Loading..." : "No leads found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Lead Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-surface border-platinum/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-cream">Create New Lead</DialogTitle>
            <DialogDescription className="text-platinum-dark">Add a new lead to the pipeline</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-platinum">Name *</Label>
                <Input 
                  value={newLead.name}
                  onChange={(e) => setNewLead({...newLead, name: e.target.value})}
                  className="bg-surface-highlight border-platinum/20"
                  placeholder="Full name"
                />
              </div>
              <div>
                {/* CAPMARKETS-293: email is no longer required on its own —
                    a lead reached by phone is still a lead. */}
                <Label className="text-platinum">Email</Label>
                <Input 
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                  className="bg-surface-highlight border-platinum/20"
                  placeholder="email@example.com"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-platinum">Phone</Label>
                <Input
                  value={newLead.phone}
                  onChange={(e) => setNewLead({...newLead, phone: e.target.value})}
                  className="bg-surface-highlight border-platinum/20"
                  placeholder="+1 (555) 000-0000"
                />
                {/* CAPMARKETS-293: the rule is "at least one of email/phone",
                    so it belongs to the pair rather than to either field.

                    `text-platinum-dark`, NOT `text-muted-foreground`: Dialog
                    renders through DialogPortal into document.body, OUTSIDE
                    `.dashboard-shell`, so the dark-theme lightening rule in
                    index.css never reaches it and the class would resolve to a
                    fixed slate-500 — 3.50:1 on the dark dialog. That same portal
                    fact is why the light-theme remediation missed this dialog and
                    is the root cause of the bug this ticket fixes. */}
                <p className="text-xs text-platinum-dark mt-1">
                  Email or phone — at least one is required.
                </p>
              </div>
              <div>
                <Label className="text-platinum">Company</Label>
                <Input 
                  value={newLead.company}
                  onChange={(e) => setNewLead({...newLead, company: e.target.value})}
                  className="bg-surface-highlight border-platinum/20"
                  placeholder="Company name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-platinum">Source</Label>
                <Select value={newLead.source} onValueChange={(v) => setNewLead({...newLead, source: v})}>
                  <SelectTrigger className="bg-surface-highlight border-platinum/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-platinum/10">
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="direct">Direct</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-platinum">Capital Band</Label>
                <Select value={newLead.capital_band} onValueChange={(v) => setNewLead({...newLead, capital_band: v})}>
                  <SelectTrigger className="bg-surface-highlight border-platinum/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-platinum/10">
                    {enums?.capital_bands?.map(b => (
                      <SelectItem key={b} value={b}>{enums.capital_band_labels[b]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-platinum">Notes</Label>
              <Textarea 
                value={newLead.notes}
                onChange={(e) => setNewLead({...newLead, notes: e.target.value})}
                className="bg-surface-highlight border-platinum/20"
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-platinum/20">
              Cancel
            </Button>
            <Button onClick={createLead} className="bg-burgundy hover:bg-burgundy-light">
              Create Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-surface border-platinum/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-cream flex items-center gap-3">
              {selectedLead?.name || 'Lead Details'}
              {selectedLead && getStatusBadgeInDialog(selectedLead.lead_status)}
            </DialogTitle>
            <DialogDescription className="text-platinum-dark">{selectedLead?.email}</DialogDescription>
          </DialogHeader>
          
          {selectedLead && (
            <Tabs defaultValue="overview" className="mt-4">
              <TabsList className="bg-surface-highlight border-platinum/10 text-platinum-dark">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="activity">Activity</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="space-y-4 mt-4">
                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-surface-highlight border-platinum/10">
                    <CardContent className="p-4 space-y-3">
                      {/* CAPMARKETS-293: conditional, like its two siblings.
                          Unconditional, a phone-only lead rendered a mail icon
                          followed by empty space. */}
                      {selectedLead.email && (
                        <div className="flex items-center gap-2 text-platinum-dark">
                          <Mail size={16} />
                          <span className="text-cream">{selectedLead.email}</span>
                        </div>
                      )}
                      {selectedLead.phone && (
                        <div className="flex items-center gap-2 text-platinum-dark">
                          <Phone size={16} />
                          <span className="text-cream">{selectedLead.phone}</span>
                        </div>
                      )}
                      {selectedLead.company && (
                        <div className="flex items-center gap-2 text-platinum-dark">
                          <Building size={16} />
                          <span className="text-cream">{selectedLead.company}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-surface-highlight border-platinum/10">
                    <CardContent className="p-4 space-y-3">
                      <div>
                        <p className="text-platinum-dark text-sm">Capital Band</p>
                        {getCapitalBadgeInDialog(selectedLead.capital_band)}
                      </div>
                      <div>
                        <p className="text-platinum-dark text-sm">Source</p>
                        <p className="text-cream capitalize">{selectedLead.source || "—"}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Ownership */}
                {platformCrm ? (
                  /* EchoSense CRM path: introducer = the party's current
                     ownership row (008 subject = capmarkets user id),
                     read-only — ownership reassignment on the platform
                     ships with STARSEED-CRM-018. */
                  <Card className="bg-surface-highlight border-platinum/10">
                    <CardContent className="p-4">
                      <Label className="text-platinum-dark text-sm">Introducer</Label>
                      <p className="text-cream mt-1" data-testid="crm-introducer">
                        {(() => {
                          const subject = currentIntroducerSubject(platformDetail);
                          if (!subject) return "None";
                          const match =
                            assignableUsers.introducers?.find(u => u.id === subject) ||
                            assignableUsers.owners?.find(u => u.id === subject);
                          return match?.full_name || subject;
                        })()}
                      </p>
                      <p className="text-platinum-dark text-xs mt-2">
                        Ownership changes on EchoSense CRM ship with STARSEED-CRM-018.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                <Card className="bg-surface-highlight border-platinum/10">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-platinum-dark text-sm">Assigned Owner</Label>
                        {isAdmin ? (
                          <Select 
                            value={selectedLead.assigned_owner_user_id || "unassigned"}
                            onValueChange={(v) => updateLeadField(selectedLead.id, "assigned_owner_user_id", v === "unassigned" ? null : v)}
                          >
                            <SelectTrigger className="bg-surface border-platinum/20 text-platinum mt-1">
                              <SelectValue placeholder="Select owner" />
                            </SelectTrigger>
                            <SelectContent className="bg-surface border-platinum/10">
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {assignableUsers.owners?.map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-cream mt-1">
                            {assignableUsers.owners?.find(u => u.id === selectedLead.assigned_owner_user_id)?.full_name || 'Unassigned'}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label className="text-platinum-dark text-sm flex items-center gap-2">
                          Introducer
                          {selectedLead.introducer_locked && (
                            <span className="text-gold-dark flex items-center gap-1">
                              <Lock size={12} /> Locked
                            </span>
                          )}
                        </Label>
                        {isAdmin && user?.role === "admin" ? (
                          <Select 
                            value={selectedLead.introducer_user_id || "none"}
                            onValueChange={(v) => updateLeadField(selectedLead.id, "introducer_user_id", v === "none" ? null : v)}
                            disabled={selectedLead.introducer_locked && user?.role !== "admin"}
                          >
                            <SelectTrigger className="bg-surface border-platinum/20 text-platinum mt-1">
                              <SelectValue placeholder="Select introducer" />
                            </SelectTrigger>
                            <SelectContent className="bg-surface border-platinum/10">
                              <SelectItem value="none">None</SelectItem>
                              {assignableUsers.introducers?.map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.email})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-cream mt-1">
                            {assignableUsers.introducers?.find(u => u.id === selectedLead.introducer_user_id)?.full_name || 'None'}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                )}

                {/* Notes */}
                {platformCrm ? (
                  /* EchoSense CRM notes (admin tier visible in full per the
                     admin matrix column; rendered as returned). */
                  <Card className="bg-surface-highlight border-platinum/10">
                    <CardContent className="p-4 space-y-3">
                      <Label className="text-platinum-dark text-sm">Notes</Label>
                      {platformNotes.length === 0 && (
                        <p className="text-platinum-dark text-sm">No notes yet</p>
                      )}
                      {platformNotes.map((note) => (
                        <div key={note.id} className="p-2 bg-surface rounded border border-platinum/10">
                          <p className="text-cream text-sm">{note.body}</p>
                          <p className="text-platinum-dark text-xs mt-1">
                            {formatDate(note.createdAt)}
                            {note.visibilityTier === "admin" ? " · admin" : ""}
                          </p>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <Textarea
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          className="bg-surface border-platinum/20 text-platinum flex-1"
                          placeholder="Add an admin note…"
                          rows={2}
                          data-testid="crm-note-input"
                        />
                        <Button
                          onClick={addPlatformNote}
                          disabled={addingNote || !newNote.trim()}
                          className="bg-burgundy hover:bg-burgundy-light self-end"
                          data-testid="crm-note-add"
                        >
                          {addingNote ? "Adding…" : "Add"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  selectedLead.notes && (
                    <Card className="bg-surface-highlight border-platinum/10">
                      <CardContent className="p-4">
                        <Label className="text-platinum-dark text-sm">Notes</Label>
                        <p className="text-cream mt-1">{selectedLead.notes}</p>
                      </CardContent>
                    </Card>
                  )
                )}
                
                {/* Status Change Button */}
                <Button 
                  onClick={() => { setShowDetailModal(false); setShowStatusModal(true); }}
                  className="w-full bg-burgundy hover:bg-burgundy-light"
                >
                  <ChevronRight size={16} className="mr-2" />
                  Change Status
                </Button>
              </TabsContent>
              
              <TabsContent value="activity" className="mt-4">
                <ActivityTimeline
                  leadId={platformCrm ? undefined : selectedLead?.id}
                  crmPartyId={platformCrm ? selectedLead?.id : undefined}
                  compact={true}
                />
              </TabsContent>

              <TabsContent value="history" className="mt-4">
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {platformCrm ? (
                    <>
                      {platformHistory.length === 0 && (
                        <p className="text-platinum-dark text-sm">
                          No stage changes recorded on EchoSense CRM yet.
                        </p>
                      )}
                      {platformHistory.map((entry) => (
                        <div key={entry.id} className="flex items-start gap-3 p-3 bg-surface-highlight rounded border border-platinum/10">
                          <History size={16} className="text-burgundy mt-1" />
                          <div>
                            <p className="text-cream text-sm">{entry.subject}</p>
                            {entry.description && <p className="text-platinum-dark text-sm">{entry.description}</p>}
                            <p className="text-platinum-dark text-xs">
                              {formatDate(entry.created_at)}
                              {entry.created_by_name ? ` · ${entry.created_by_name}` : ""}
                            </p>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    selectedLead.status_history?.slice().reverse().map((history, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-surface-highlight rounded border border-platinum/10">
                        <History size={16} className="text-burgundy mt-1" />
                        <div>
                          <p className="text-cream text-sm">
                            {enums?.lead_status_labels[history.status] || history.status}
                          </p>
                          {history.note && <p className="text-platinum-dark text-sm">{history.note}</p>}
                          <p className="text-platinum-dark text-xs">{formatDate(history.timestamp)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Transition Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="bg-surface border-platinum/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cream">Update Lead Status</DialogTitle>
            <DialogDescription className="text-platinum-dark">
              Current: {enums?.lead_status_labels[selectedLead?.lead_status] || selectedLead?.lead_status}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-platinum">New Status *</Label>
              <Select 
                value={statusTransition.new_status} 
                onValueChange={(v) => setStatusTransition({...statusTransition, new_status: v})}
              >
                <SelectTrigger className="bg-surface-highlight border-platinum/20 mt-1">
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-platinum/10">
                  {enums?.lead_statuses?.map(s => (
                    <SelectItem 
                      key={s} 
                      value={s}
                      disabled={s === selectedLead?.lead_status}
                    >
                      {enums.lead_status_labels[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Validation warnings */}
            {statusTransition.new_status && (
              <div className="space-y-2">
                {!platformCrm && statusTransition.new_status !== "new_uncontacted" && !selectedLead?.assigned_owner_user_id && (
                  <p className="text-gold-dark text-sm flex items-center gap-2">
                    <AlertTriangle size={14} />
                    Owner assignment required for this status
                  </p>
                )}
                {["qualified_lead", "account_setup", "funded_initial", "active_client", "strategic_client"].includes(statusTransition.new_status) &&
                 (platformCrm
                   ? (!selectedLead?.capital_band || selectedLead?.capital_band === "unknown")
                   : selectedLead?.capital_band === "unknown") && (
                  <p className="text-gold-dark text-sm flex items-center gap-2">
                    <AlertTriangle size={14} />
                    Capital band must be specified
                  </p>
                )}
                {!platformCrm && statusTransition.new_status === "qualified_lead" && !selectedLead?.introducer_locked && (
                  <p className="text-azure text-sm flex items-center gap-2">
                    <Lock size={14} />
                    Introducer will be locked at this status
                  </p>
                )}
              </div>
            )}
            
            <div>
              <Label className="text-platinum">Note (optional)</Label>
              <Textarea 
                value={statusTransition.note}
                onChange={(e) => setStatusTransition({...statusTransition, note: e.target.value})}
                className="bg-surface-highlight border-platinum/20 mt-1"
                placeholder="Add a note about this status change..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusModal(false)} className="border-platinum/20">
              Cancel
            </Button>
            <Button
              onClick={updateLeadStatus}
              disabled={
                !statusTransition.new_status ||
                /* Platform path: the capital-band pipeline rule is enforced
                   here at the submit (the legacy 400 lived in
                   routes/managed_leads.py:314-316; the platform stage
                   endpoint carries no legacy pipeline rules —
                   server-side rule parity is STARSEED-CRM-019). */
                (platformCrm &&
                  ["qualified_lead", "account_setup", "funded_initial", "active_client", "strategic_client"].includes(statusTransition.new_status) &&
                  (!selectedLead?.capital_band || selectedLead?.capital_band === "unknown"))
              }
              className="bg-burgundy hover:bg-burgundy-light"
            >
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default LeadManagementPage;
